import React, { useCallback, useEffect, useState } from "react";
import { usePageMeta } from "../lib/usePageMeta";
import { blogIndexSeo, fetchBlogPosts, opinlyConfig } from "../lib/opinly";
import { PostCard } from "../components/blog/PostCard";
import { BlogEmpty, BlogError, BlogListSkeleton } from "../components/blog/BlogStates";

export default function BlogIndex() {
  usePageMeta(blogIndexSeo());
  const [state, setState] = useState({ status: "loading", posts: [], cursor: null, hasMore: false, imagesPrefix: null });
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(() => {
    setState((s) => ({ ...s, status: "loading" }));
    fetchBlogPosts()
      .then((d) => setState({ status: "ready", posts: d.posts, cursor: d.next_cursor, hasMore: d.has_more, imagesPrefix: d.images_prefix }))
      .catch(() => setState((s) => ({ ...s, status: "error" })));
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadMore = () => {
    setLoadingMore(true);
    fetchBlogPosts(state.cursor)
      .then((d) => setState((s) => ({ ...s, posts: [...s.posts, ...d.posts], cursor: d.next_cursor, hasMore: d.has_more })))
      .catch(() => setState((s) => ({ ...s, hasMore: false })))
      .finally(() => setLoadingMore(false));
  };

  const config = opinlyConfig(state.imagesPrefix);

  return (
    <div className="bg-white" data-testid="blog-index-page">
      <section className="bg-brand-surface border-b border-brand-border">
        <div className="mps-container py-16 md:py-20 max-w-4xl">
          <div className="eyebrow mb-4">Blog</div>
          <h1 data-testid="blog-index-h1" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-navy leading-tight">
            Research notes &amp; reference guides.
          </h1>
          <p className="mt-6 text-brand-muted text-base md:text-lg leading-relaxed max-w-2xl">
            Plain-English articles on reference standards, batch documentation, storage, and laboratory handling.
            Written for researchers. <strong className="text-brand-navy">For research use only.</strong>
          </p>
        </div>
      </section>

      <section className="mps-container py-14 md:py-20">
        {state.status === "loading" && <BlogListSkeleton />}
        {state.status === "error" && <BlogError onRetry={load} />}
        {state.status === "ready" && state.posts.length === 0 && <BlogEmpty />}
        {state.status === "ready" && state.posts.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-post-list">
              {state.posts.map((p, i) => <PostCard key={p.slug} post={p} config={config} featured={i === 0} />)}
            </div>
            {state.hasMore && (
              <div className="mt-12 text-center">
                <button type="button" onClick={loadMore} disabled={loadingMore} data-testid="blog-load-more-btn" className="inline-flex h-12 items-center px-7 rounded-[12px] border border-brand-border text-brand-navy hover:bg-brand-surface font-semibold text-sm transition-colors disabled:opacity-60">
                  {loadingMore ? "Loading…" : "Load more articles"}
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
